/**
 * Policy Navigator Layout Component
 *
 * Provides the main layout structure including:
 * - Shared AppHeader with role-based navigation
 * - Content area for chat interface
 * - Integrates with unified authentication system
 */

import styles from "./Layout.module.css";
import {
  ShieldLockRegular,
  AddRegular,
} from "@fluentui/react-icons";
import { Stack } from "@fluentui/react";
import { ReactNode, useEffect, useState } from "react";
import { AppHeader } from "../../../../shared/src/components";
import { checkAuthEnforced } from "../../api";
import { useAuth } from "../../../../shared/src/auth";
import SpinnerComponent from '../../components/Spinner/Spinner';


export type LayoutProps = {
  children: ReactNode;
  toggleSpinner: boolean;
  onSetShowHistoryPanel: () => void;
  showHistoryBtn: boolean;
  showHistoryPanel: boolean;
  onNewChat?: () => void;
};
const Layout = ({ children, toggleSpinner, onNewChat }: LayoutProps) => {
  const { isAuthenticated, isLoading } = useAuth();

  const [showAuthMessage, setShowAuthMessage] = useState<boolean | undefined>();

  const checkAuthConfiguration = async () => {
    try {
      const isAuthEnforced = await checkAuthEnforced();

      if (!isAuthEnforced) {
        setShowAuthMessage(false);
      } else {
        setShowAuthMessage(isAuthEnforced && !isAuthenticated && !isLoading);
      }
    } catch (error) {
      console.error("Failed to check auth configuration:", error);
      setShowAuthMessage(!isAuthenticated && !isLoading);
    }
  };

  useEffect(() => {
    checkAuthConfiguration();
  }, [isAuthenticated, isLoading]);

  return (
    <div className={styles.layout}>
      <SpinnerComponent
        loading={toggleSpinner}
        label="Please wait.....!"
      />
      <AppHeader
        currentApp="policy_navigator"
        actions={
          onNewChat && (
            <button
              className={styles.newChatButton}
              onClick={onNewChat}
              aria-label="New Chat"
            >
              <AddRegular />
              <span>New Chat</span>
            </button>
          )
        }
      />
      {showAuthMessage ? (
        <Stack className={styles.chatEmptyState}>
          <ShieldLockRegular
            className={styles.chatIcon}
            style={{ color: "darkorange", height: "200px", width: "200px" }}
          />
          <h1 className={styles.chatEmptyStateTitle}>
            Authentication Not Configured
          </h1>
          <h2 className={styles.chatEmptyStateSubtitle}>
            This app does not have authentication configured. Please add an
            identity provider by finding your app in the{" "}
            <a href="https://portal.azure.com/" target="_blank">
              Azure Portal
            </a>{" "}
            and following{" "}
            <a
              href="https://learn.microsoft.com/en-us/azure/app-service/scenario-secure-app-authentication-app-service#3-configure-authentication-and-authorization"
              target="_blank"
            >
              these instructions
            </a>
            .
          </h2>
          <h2
            className={styles.chatEmptyStateSubtitle}
            style={{ fontSize: "20px" }}
          >
            <strong>
              Authentication configuration takes a few minutes to apply.{" "}
            </strong>
          </h2>
          <h2
            className={styles.chatEmptyStateSubtitle}
            style={{ fontSize: "20px" }}
          >
            <strong>
              If you deployed in the last 10 minutes, please wait and reload the
              page after 10 minutes.
            </strong>
          </h2>
        </Stack>
      ) : (
        <div className={styles.content}>
          {children}
        </div>
      )}
    </div>
  );
};

export default Layout;
